import type { Urgency } from '../../types';
import { primaryProfile } from '../../lib/v1';
import type { V1User } from '../../lib/v1';
import { buildPath } from '../../lib/route';
import {
  wiedervorlageState, wiedervorlageHighlight, type WiedervorlageTone,
} from '../pflege/wiedervorlage';

export const URG_TONE: Record<Urgency, string> = {
  'Hoch': 'error', 'Mittel': 'warning', 'Niedrig': 'neutral',
};

export const URG_LABEL_KEY: Record<string, string> = {
  'Hoch': 'anfragen.list.priority.high', 'Mittel': 'anfragen.list.priority.medium', 'Niedrig': 'anfragen.list.priority.low',
};

// Maps PB konto value to a 0-based phase index. Scales with however many phases exist.
export function kontoToPhaseIdx(konto: number, phaseCount: number): number {
  if (konto <= 1) return 0;
  if (konto === 2) return Math.min(1, phaseCount - 1);
  if (konto === 3) return Math.min(2, phaseCount - 1);
  return phaseCount - 1; // konto 4 (Vermittelt) → last phase
}

export const URG_FILTERS: { value: string; labelKey: string }[] = [
  { value: 'Alle',    labelKey: 'anfragen.list.priority.all' },
  { value: 'Hoch',    labelKey: 'anfragen.list.priority.high' },
  { value: 'Mittel',  labelKey: 'anfragen.list.priority.medium' },
  { value: 'Niedrig', labelKey: 'anfragen.list.priority.low' },
];

/**
 * Auflösungs-Info je Anfrage: welches Familienkonto + welche Person gehört zu
 * dem PB-User? Aus dem rohen V1User abgeleitet (family_account_id +
 * profiles[0].id). Ist `accountId` null (Alt-PB ohne migriertes Familienkonto),
 * kann die Voll-Detailansicht nicht geöffnet werden.
 *
 * `userId` (users.id des Pflegeempfängers) ist seit 23.09.2026 (Entscheidung
 * Damon: users.id führt überall, auch in der URL) das Feld, das die Detail-
 * Navigation tatsächlich braucht (`pbHref`/`onOpenPb`). `accountId`/
 * `profileId` bleiben zusätzlich erhalten — PflegeClientDetail.tsx (Kontakte-
 * Tab) braucht weiterhin den accountId, um die übrigen Kontomitglieder zu
 * laden.
 */
export interface PbRef {
  accountId: number | null;
  profileId: number | null;
  userId: number | null;
}

/**
 * Liest family_account_id + users.id (Pflegeempfänger) aus dem rohen V1User.
 * Die getUsersByRole-Antwort serialisiert das komplette User-Model (inkl.
 * family_account_id) und hängt `profiles[]` an — beide werden vom Anfrage-Typ
 * nicht getragen, hier aber zum Deep-Link/Detail-Öffnen benötigt.
 */
export function pbRefFromUser(u: V1User): PbRef {
  const rawAccount = (u as { family_account_id?: number | string | null }).family_account_id;
  const accountId =
    typeof rawAccount === 'number' ? rawAccount
    : typeof rawAccount === 'string' && /^\d+$/.test(rawAccount) ? Number(rawAccount)
    : null;

  // Direktlink/„Profil öffnen" soll IMMER auf das Pflegeempfänger-Profil
  // zeigen, nicht auf `u` selbst — `u` kann im Familienkonto eine
  // Kontaktperson mit eigenem Account sein (z. B. "Max junior"), während der
  // eigentliche Pflegeempfänger ("Max Mustermann") ein anderes Konto-
  // Mitglied ist. Bevorzugt: vom Backend aufgelöst (PbListController::
  // enrichRows, additiv) — deckt auch fremde Accounts ab, kein Extra-Request.
  if (u.pe_user_id != null && u.pe_profile_id != null) {
    return { accountId, profileId: u.pe_profile_id, userId: u.pe_user_id };
  }

  // Fallback ohne pe_*-Felder (z. B. ältere Antwort ohne Familienkonto-
  // Anreicherung): eigene profiles-Liste nach is_pflegeempfaenger durchsuchen,
  // sonst primäres Profil. userId ist dann `u.id` selbst (keine PE-Auflösung
  // bekannt — u IST die Person, um die es geht).
  const profiles = (u as { profiles?: Array<{ id?: number; is_pflegeempfaenger?: unknown }> }).profiles ?? [];
  const pe = profiles.find(p => Boolean(p?.is_pflegeempfaenger));
  const profileId = (pe?.id ?? primaryProfile(u)?.id) ?? null;
  return { accountId, profileId, userId: u.id ?? null };
}

/** Deep-Link (kopierbare URL) auf die volle PB-Profilseite oder null. */
export function pbHref(ref: PbRef | undefined): string | null {
  if (!ref || ref.accountId == null || ref.userId == null) return null;
  return buildPath('pflege', String(ref.userId));
}

// Server-seitige Pagination: die PB-"Pipeline" hat mehrere Tausend Einträge.
export const ANFRAGEN_PER_PAGE = 50;

// Tabellen-Spalte → serverseitiges Sortierfeld. `profile` = Spalte auf der
// jointen profiles-Tabelle. Abgeleitete Spalten (need/urgency/budget) sind
// nicht serverseitig sortierbar.
export const ANFRAGEN_SORT_FIELDS: Record<string, { field: string; profile?: boolean }> = {
  id:            { field: 'id' },
  family:        { field: 'nachname', profile: true },
  city:          { field: 'ort', profile: true },
  care_level:    { field: 'pflegegrad', profile: true },
  status:        { field: 'konto' },
  received:      { field: 'created_at' },
  // Batch-13 — Wiedervorlage: getUsersByRole() fällt für unbekannte Felder auf
  // $query->orderBy($field) zurück, sortiert hier also direkt serverseitig
  // über die echte users.wiedervorlage_am-Spalte (kein v1-Edit nötig).
  wiedervorlage: { field: 'wiedervorlage_am' },
};

// Reihenfolge + i18n-Key der Tabellen-Header (sortKey verweist in SORT_FIELDS).
export const ANFRAGEN_TABLE_HEADERS: { key: string; sortKey?: string }[] = [
  { key: 'id', sortKey: 'id' },
  { key: 'family', sortKey: 'family' },
  { key: 'city', sortKey: 'city' },
  { key: 'need' },
  { key: 'care_level', sortKey: 'care_level' },
  { key: 'budget' },
  { key: 'status', sortKey: 'status' },
  { key: 'received', sortKey: 'received' },
  // ACHTUNG: Diese Reihenfolge MUSS der Reihenfolge der <td> in
  // AnfragenPage.tsx entsprechen, sonst stehen Werte unter dem falschen Kopf
  // (und der Sortier-Klick trifft die falsche Spalte). Genau das war am
  // 18.09.2026 der Fall: Kopf assigned_to/wiedervorlage, Zellen umgekehrt.
  // tests/unit/anfragen-spalten.test.ts nagelt die Uebereinstimmung fest.
  { key: 'wiedervorlage', sortKey: 'wiedervorlage' },
  { key: 'assigned_to' },
];

/**
 * Batch-13 — Wiedervorlage: "heute fällig" / "überfällig" gegen ein
 * YYYY-MM-DD-Datum (wiedervorlage_am ist ein reines Datum, keine Uhrzeit —
 * String-Vergleich reicht, spart Zeitzonen-Fallen gegenüber Date-Parsing).
 */
export function todayISODate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Beide Prädikate sind dünne Hüllen um `wiedervorlageState()` — die Datumsregel
 * gibt es nur EINMAL (src/pages/pflege/wiedervorlage.ts), sonst können Liste
 * und Drawer auseinanderlaufen. Der `today`-Parameter bleibt für Tests und
 * bestehende Aufrufer erhalten.
 */
export function isWiedervorlageOverdue(wiedervorlageAm: string | null | undefined, today = todayISODate()): boolean {
  return wiedervorlageState(wiedervorlageAm, isoToDate(today)) === 'overdue';
}

export function isWiedervorlageDueToday(wiedervorlageAm: string | null | undefined, today = todayISODate()): boolean {
  return wiedervorlageState(wiedervorlageAm, isoToDate(today)) === 'today';
}

/** yyyy-mm-dd → lokales Date (Mittag, damit keine Zeitzone den Tag kippt). */
function isoToDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1, 12, 0, 0);
}

/**
 * Zeilen-/Karten-Markierung der Anfragen-Liste: Wiedervorlage erreicht = gelb,
 * überschritten = rot. Farben und Zustandslogik kommen aus
 * `pages/pflege/wiedervorlage.ts`, damit Liste und Drawer dieselbe Definition
 * von „fällig" und denselben Farbton benutzen.
 *
 * Liefert undefined, wenn nichts zu markieren ist — dann bleibt der normale
 * Kartenhintergrund stehen.
 */
export function wiedervorlageRowTone(
  wiedervorlageAm: string | null | undefined,
  dark = false,
): WiedervorlageTone | undefined {
  return wiedervorlageHighlight(wiedervorlageState(wiedervorlageAm), dark);
}
